
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, User, Shield, LogOut, Menu, X, ShoppingBag, MessageCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import NavigationMenu from "@/components/header/NavigationMenu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import ChatOption from "@/components/ChatOption";

type MobileNavBarProps = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  isMenuOpen: boolean;
  toggleMenu: () => void;
  handleLogout: () => Promise<void>;
};

const MobileNavBar: React.FC<MobileNavBarProps> = ({
  isAuthenticated,
  isAdmin,
  isLoading,
  isMenuOpen,
  toggleMenu,
  handleLogout
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  // Don't show chat on landing pages
  const isLandingPage = location.pathname.startsWith('/landing/');

  // Common Button Style
  const navButtonClass = "flex flex-col items-center justify-center w-16 h-full";
  const activeButtonClass = `${navButtonClass} text-corporate-secondary-100`;
  const inactiveButtonClass = `${navButtonClass} text-white/80 hover:text-white`;

  // Determine if a route is active
  const isActive = (path: string) => {
    return currentPath === path;
  };

  return (
    <>
      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleMenu}
        />
      )}
      
      {/* Mobile Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 h-14 bg-corporate-primary-100 border-t border-corporate-primary-75 z-50 px-2 md:hidden">
        <div className="flex items-center justify-between h-full max-w-md mx-auto">
          {/* Home link always visible */}
          <Link
            to="/"
            className={isActive("/") ? activeButtonClass : inactiveButtonClass}
          >
            <Home className="h-5 w-5" />
            <span className="text-xs mt-1">Home</span>
          </Link>

          {/* Marketplace link always visible */}
          <Link
            to="/marketplace"
            className={isActive("/marketplace") ? activeButtonClass : inactiveButtonClass}
          >
            <ShoppingBag className="h-5 w-5" />
            <span className="text-xs mt-1">Market</span>
          </Link>

          {/* Conditional buttons based on authentication */}
          {isAuthenticated ? (
            <>
              <Link
                to="/account"
                className={isActive("/account") ? activeButtonClass : inactiveButtonClass}
              >
                <User className="h-5 w-5" />
                <span className="text-xs mt-1">Account</span>
              </Link>

              {/* Chat button - directly opens the chat */}
              {!isLandingPage && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button className={inactiveButtonClass}>
                      <MessageCircle className="h-5 w-5" />
                      <span className="text-xs mt-1">Ask Me</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-full h-[85%] bottom-14 top-auto rounded-t-xl p-0">
                    <ChatOption standalone={true} />
                  </SheetContent>
                </Sheet>
              )}

              <button
                onClick={handleLogout}
                className={inactiveButtonClass}
              >
                <LogOut className="h-5 w-5" />
                <span className="text-xs mt-1">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth"
                className={isActive("/auth") ? activeButtonClass : inactiveButtonClass}
              >
                <User className="h-5 w-5" />
                <span className="text-xs mt-1">Login</span>
              </Link>

              {/* Chat button for non-authenticated users */}
              {!isLandingPage && (
                <Sheet>
                  <SheetTrigger asChild>
                    <button className={inactiveButtonClass}>
                      <MessageCircle className="h-5 w-5" />
                      <span className="text-xs mt-1">Ask Me</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-full h-[85%] bottom-14 top-auto rounded-t-xl p-0">
                    <ChatOption standalone={true} />
                  </SheetContent>
                </Sheet>
              )}
            </>
          )}
          
          {/* Menu button - always visible */}
          <button
            onClick={toggleMenu}
            className={isMenuOpen ? activeButtonClass : inactiveButtonClass}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
            <span className="text-xs mt-1">Menu</span>
          </button>
        </div>
      </div>
      
      {/* Mobile Slide-in Menu */}
      <div 
  className={`fixed inset-y-0 right-0 w-64 bg-gradient-to-b from-corporate-primary-100 to-corporate-primary-75 z-50 transform transition-transform duration-300 ease-in-out ${
    isMenuOpen ? 'translate-x-0' : 'translate-x-full'
  } md:hidden overflow-y-auto`}
>
  {isAuthenticated && isAdmin && (
    <div className="pt-6 px-6">
      <Link
        to="/admin"
        className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white"
        onClick={toggleMenu}
      >
        <Shield className="h-5 w-5" />
        <span className="font-medium">Admin Dashboard</span>
      </Link>
    </div>
  )}
  <NavigationMenu />
</div>
    </>
  );
};

export default MobileNavBar;
